import React, { useEffect, useState } from "react";
import {
  Globe,
  MessageSquare,
  ChevronDown,
  Check,
  Trash2,
  Share2,
  Loader,
} from "lucide-react";
import Modal from "../ui/Modal";
import PhoneInput from "../PhoneInput";
import { CKEditor } from "@ckeditor/ckeditor5-react";
import ClassicEditor from "@ckeditor/ckeditor5-build-classic";
import { useExhSpaceStore } from "../../store/useExhSpaceStore";
import { useSite } from "../../store/useSite";
import { useBase } from "../../store/useBase";
import { getSiteAuthUser } from "../../services/auth";
import { useRef } from "react";
import { toast } from "react-toastify";
import { getExhibitorRequestForm, submitAdditionalProfileInfo } from "../../services/exhibitorRequest";
import ExhibitorReqFormField from "../exhibitor/exhibitor-request/ExhibitorReqFormField";

const XIcon = () => (
  <svg
    viewBox="0 0 24 24"
    className="w-3.5 h-3.5 fill-white"
    aria-hidden="true"
  >
    <path d="M18.901 1.153h3.68l-8.04 9.19L24 22.846h-7.406l-5.8-7.584-6.638 7.584H.474l8.6-9.83L0 1.154h7.594l5.243 6.932 6.064-6.932zm-1.294 19.497h2.039L6.486 3.24H4.298L17.607 20.65z"></path>
  </svg>
);

function ExhibitorProfileSettings({ isOpen, onClose, siteUser, exhibitor }) {
  const { setSiteUser } = useSite();
  const { eventId } = useBase();
  const { updateProfileSettings, submitting } = useExhSpaceStore();
  const [step, setStep] = useState(0); // 0: Personal Info, 1: Additional Info, 2: Privacy
  const fileInputRef = useRef(null);
  const [errors, setErrors] = useState({});

  const validateFile = (file, isImage = false) => {
    if (!file) return null;
    const fileName = file.name;
    const lastDotIndex = fileName.lastIndexOf(".");
    const extension = lastDotIndex !== -1 ? fileName.slice(lastDotIndex).toLowerCase() : "";
    const namePart = lastDotIndex !== -1 ? fileName.slice(0, lastDotIndex) : fileName;
    
    // Type validation
    if (isImage) {
      const allowedExtensions = [".jpg", ".jpeg", ".png", ".gif", ".webp"];
      if (!allowedExtensions.includes(extension)) {
        return "Invalid file type. Only JPG, JPEG, PNG, GIF, and WEBP are allowed.";
      }
    }

    // Name validation
    const illegalChars = /[ _.,@-]/; 
    if (illegalChars.test(namePart)) {
      return "File name should not contain spaces, underscores, dots, commas, @, or hyphens. Please rename the file.";
    }
    return null;
  };

  // Initialize with booth_member data if available, fallback to exhibitor/siteUser
  const [formData, setFormData] = useState({
    firstName:
      siteUser?.booth_member?.first_name || siteUser?.name?.split(" ")[0] || "",
    lastName:
      siteUser?.booth_member?.last_name ||
      siteUser?.name?.split(" ").slice(1).join(" ") ||
      "",
    email: siteUser?.email || "",
    company: siteUser?.booth_member?.company_name || exhibitor?.name || "",
    designation: siteUser?.booth_member?.designation || "",
    phone:
      siteUser?.booth_member?.phone || exhibitor?.primary_contact_phone || "",
    about:
      siteUser?.booth_member?.about || exhibitor?.company_description || "",
    social: "",
    selectedPlatform: "Facebook",
    socialLinks: Array.isArray(siteUser?.booth_member?.social_links)
      ? siteUser.booth_member.social_links
      : typeof siteUser?.booth_member?.social_links === "string"
        ? JSON.parse(siteUser.booth_member.social_links)
        : [],
    directMessages: true, // Placeholder for now or add column later
    dataShare: siteUser?.data_share === 1,
    profileImage: siteUser?.booth_member?.profile_image || "",
    profileImageFile: null,
    website: exhibitor?.website || "",
    companySize: "",
    industry: "",
    additional_info: (() => {
      const info = {};
      if (siteUser?.booth_member?.answers) {
        siteUser.booth_member.answers.forEach(ans => {
          if (ans.field_name) info[ans.field_name] = ans.answer;
        });
      }
      const oldInfo = siteUser?.booth_member?.additional_info;
      const parsedOldInfo = typeof oldInfo === 'string' ? JSON.parse(oldInfo) : (oldInfo || {});
      return { ...parsedOldInfo, ...info };
    })(),
  });

  const [additionalFields, setAdditionalFields] = useState([]);


  const handleAddSocialLink = () => {
    const { social, selectedPlatform, socialLinks } = formData;
    if (!social) return;

    // Force full URL
    if (!social.startsWith("http://") && !social.startsWith("https://")) {
      setErrors((prev) => ({
        ...prev,
        social: "Please paste the full URL (starting with https://)",
      }));
      return;
    }

    // Ensure unique platform
    if (socialLinks.some((l) => l.platform === selectedPlatform)) {
      setErrors((prev) => ({
        ...prev,
        social: `You have already added a ${selectedPlatform} link. Please remove the existing one first.`,
      }));
      return;
    }

    const patterns = {
      X: /^(https?:\/\/)?(www\.)?(x|twitter)\.com\/[a-zA-Z0-9_]+\/?$/,
      Facebook: /^(https?:\/\/)?(www\.)?facebook\.com\/[a-zA-Z0-9.]+\/?$/,
      LinkedIn:
        /^(https?:\/\/)?(www\.)?linkedin\.com\/(in|company|school)\/[a-zA-Z0-9-]+\/?$/,
      Instagram: /^(https?:\/\/)?(www\.)?instagram\.com\/[a-zA-Z0-9_.]+\/?$/,
      Telegram:
        /^(https?:\/\/)?(www\.)?(t\.me|telegram\.me)\/[a-zA-Z0-9_]+\/?$/,
      Medium: /^(https?:\/\/)?(www\.)?medium\.com\/@[a-zA-Z0-9_.]+\/?$/,
      TikTok: /^(https?:\/\/)?(www\.)?tiktok\.com\/@[a-zA-Z0-9_.]+\/?$/,
      YouTube:
        /^(https?:\/\/)?(www\.)?youtube\.com\/(@[a-zA-Z0-9_-]+|channel\/[a-zA-Z0-9_-]+)\/?$/,
    };

    if (!patterns[selectedPlatform].test(social)) {
      setErrors((prev) => ({
        ...prev,
        social: `The URL does not match a valid ${selectedPlatform} format.`,
      }));
      return;
    }

    setErrors((prev) => {
      const { social, ...rest } = prev;
      return rest;
    });

    setFormData({
      ...formData,
      socialLinks: [
        ...socialLinks,
        { platform: selectedPlatform, url: social },
      ],
      social: "",
    });
  };

  const removeSocialLink = (index) => {
    setFormData({
      ...formData,
      socialLinks: formData.socialLinks.filter((_, i) => i !== index),
    });
  };

  const handlePhotoClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      const fileError = validateFile(file, true); // true for isImage
      if (fileError) {
        setErrors((prev) => ({
          ...prev,
          profileImage: fileError,
        }));
        toast.error(fileError);
        return;
      }
      if (file.size > 5 * 1024 * 1024) {
        setErrors((prev) => ({
          ...prev,
          profileImage: "File size cannot exceed 5MB",
        }));
        toast.error("File size cannot exceed 5MB");
        return;
      }
      setErrors((prev) => {
        const { profileImage, ...rest } = prev;
        return rest;
      });
      const reader = new FileReader();
      reader.onloadend = () => {
        setFormData({
          ...formData,
          profileImage: reader.result,
          profileImageFile: file,
        });
      };
      reader.readAsDataURL(file);
    }
  };

  useEffect(() => {
    if (isOpen) {
      setStep(0);
      // Refresh user data when onboarding opens
      getSiteAuthUser(eventId).then((res) => {
        if (res && res.status === 200) {
          setSiteUser(res.data);
        }
      });
    }
  }, [isOpen, eventId, setSiteUser]);

  useEffect(() => {
    const fetchAdditionalFields = async () => {
      if (!eventId || !isOpen) return;
      try {
        const res = await getExhibitorRequestForm(eventId, 2);
        
        if (res.data.message === 'success') {
          setAdditionalFields(res.data.data.fields || []);
        }
      } catch (error) {
        console.error("Error fetching additional fields:", error);
      }
    };
    fetchAdditionalFields();
  }, [eventId, isOpen]);
  
  // Sync state with siteUser if it changes (e.g. after refresh)
  useEffect(() => {
    if (siteUser?.booth_member) {
      const info = {};
      if (siteUser.booth_member.answers) {
        siteUser.booth_member.answers.forEach(ans => {
          if (ans.field_name) info[ans.field_name] = ans.answer;
        });
      }
      const oldInfo = siteUser.booth_member.additional_info;
      const parsedOldInfo = typeof oldInfo === 'string' ? JSON.parse(oldInfo) : (oldInfo || {});

      setFormData(prev => ({
        ...prev,
        firstName: siteUser.booth_member.first_name || siteUser.name?.split(" ")[0] || prev.firstName,
        lastName: siteUser.booth_member.last_name || siteUser.name?.split(" ").slice(1).join(" ") || prev.lastName,
        email: siteUser.email || prev.email,
        company: siteUser.booth_member.company_name || prev.company,
        designation: siteUser.booth_member.designation || prev.designation,
        phone: siteUser.booth_member.phone || prev.phone,
        about: siteUser.booth_member.about || prev.about,
        profileImage: siteUser.booth_member.profile_image || prev.profileImage,
        additional_info: { ...parsedOldInfo, ...info }
      }));
    }
  }, [siteUser]);

  if (!isOpen) return null;

  const handleDone = async () => {
    const newErrors = {};
    if (!formData.firstName?.trim()) {
      newErrors.firstName = "First Name is required";
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      setStep(0); // Go to personal info if there are errors there
      toast.error("Please fix the validation errors");
      return;
    }

    const memberId = siteUser?.booth_member?.id;
    if (!memberId) {
      toast.error("Member ID not found. Internal data error.");
      return;
    }

    let payload;
    if (formData.profileImageFile) {
      payload = new FormData();
      payload.append("first_name", formData.firstName || "");
      payload.append("last_name", formData.lastName || "");
      payload.append("email", formData.email || "");
      if (formData.phone) payload.append("phone", formData.phone);
      if (formData.designation)
        payload.append("designation", formData.designation);
      if (formData.about) payload.append("about", formData.about);
      payload.append(
        "social_links",
        JSON.stringify(formData.socialLinks || []),
      );
      payload.append("data_share", formData.dataShare ? 1 : 0);
      payload.append("profile_image", formData.profileImageFile);
      if (formData.company) payload.append("company_name", formData.company);
      if (siteUser?.id) payload.append("site_user_id", siteUser.id);
      payload.append("additional_info", JSON.stringify(formData.additional_info || {}));
    } else {
      payload = {
        first_name: formData.firstName,
        last_name: formData.lastName,
        email: formData.email,
        phone: formData.phone,
        designation: formData.designation,
        about: formData.about,
        social_links: formData.socialLinks,
        data_share: formData.dataShare ? 1 : 0,
        profile_image: formData.profileImage,
        company_name: formData.company,
        site_user_id: siteUser?.id,
        additional_info: formData.additional_info,
      };
    }

    const success = await updateProfileSettings(memberId, payload);
    if (success) {
      // Also submit dynamic fields as a formal form submission (Context 2)
      const userRoleId = siteUser?.role_id || siteUser?.roleId || siteUser?.booth_member?.role_id;
      if (additionalFields.length > 0 && userRoleId === 23) {
        try {
          const formDataObj = new FormData();
          formDataObj.append("event_id", eventId);
          formDataObj.append("member_id", memberId);

          let imageCounter = 0;
          const answers = additionalFields.map((field) => {
            const val = formData.additional_info[field.name];
            let finalValue = val || "";

            if ((field.type === "image" || field.type === "file") && val instanceof File) {
              const fieldName = `image_${imageCounter++}`;
              formDataObj.append(fieldName, val);
              finalValue = fieldName;
            }

            return {
              field_id: field.id,
              value: finalValue,
            };
          });

          formDataObj.append("answers", JSON.stringify(answers));
          await submitAdditionalProfileInfo(formDataObj);
        } catch (error) {
          console.error("Error submitting additional info history:", error);
        }
      }

      const userRes = await getSiteAuthUser(eventId);
      if (userRes && userRes.status === 200) {
        setSiteUser(userRes.data);
      }
      onClose();
    }
  };

  const sections = [
    { label: "Personal Information", id: 0 },
    { label: "Additional Information", id: 1 },
    { label: "Privacy Preferences", id: 2 },
  ];

  const Header = ({ currentStep }) => (
    <div className="flex flex-col w-full">
      <div className="flex items-center gap-3 mb-3">
        <h1 className="section-heading">Profile Settings</h1>
        <span className="px-2.5 py-1 bg-primary-bg-color/10 text-primary-bg-color rounded-full text-[10px] font-bold tracking-wider uppercase border border-primary-bg-color/20">
          Exhibitor
        </span>
      </div>

      <div className="flex items-center gap-x-8 border-b border-gray-100">
        {sections.map((tab) => (
          <div
            key={tab.id}
            className="pb-3 relative transition-all cursor-pointer group"
            onClick={() => setStep(tab.id)}
          >
            <span
              className={`text-sm font-semibold transition-all ${currentStep === tab.id ? "text-primary-bg-color" : "text-gray-500 group-hover:text-gray-700"}`}
            >
              {tab.label}
            </span>
            {currentStep === tab.id && (
              <div className="absolute bottom-0 left-0 w-full h-0.5 bg-primary-bg-color rounded-t-full" />
            )}
          </div>
        ))}
      </div>
    </div>
  );

  const Footer = () => (
    <div className="flex justify-end items-center gap-3 w-full">
      <button onClick={onClose} className="form-cancel-btn">
        Close
      </button>
      <button
        onClick={handleDone}
        disabled={submitting}
        className={`form-primary-btn flex gap-x-2 items-center min-w-[100px] justify-center ${submitting ? "opacity-70 cursor-not-allowed" : ""}`}
      >
        {submitting ? (
          <>
            <Loader className="animate-spin h-4 w-4" />
            <span>Saving...</span>
          </>
        ) : (
          "Save Changes"
        )}
      </button>
    </div>
  );

  const renderInfo = () => (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      maxWidth="max-w-5xl"
      title={<Header currentStep={0} />}
      footer={<Footer />}
    >
      <div className="grid grid-cols-1 lg:grid-cols-[1.2fr,1.3fr] gap-x-10 gap-y-6 min-h-[480px]">
        {/* Left Column */}
        <section className="flex flex-col gap-6">
          <div className="flex flex-col gap-2">
            <label className="form-label">Profile Photo</label>
            <div className="flex items-start gap-6">
              <div className="w-20 h-20 bg-primary-bg-color rounded-2xl flex items-center justify-center text-white text-3xl font-bold shadow-md overflow-hidden ring-4 ring-primary-bg-color/10 flex-shrink-0">
                {formData.profileImage ? (
                  <img
                    src={formData.profileImage}
                    alt="Profile"
                    className="w-full h-full object-cover"
                  />
                ) : (
                  formData.firstName?.charAt(0) || "U"
                )}
              </div>
              <div className="flex flex-col gap-3">
                <div className="flex flex-col gap-2">
                  <input
                    type="file"
                    ref={fileInputRef}
                    className="hidden"
                    accept="image/jpeg, image/png, image/gif, image/webp"
                    onChange={handleFileChange}
                  />
                  <button
                    onClick={handlePhotoClick}
                    className="text-primary-bg-color font-semibold text-sm hover:underline transition-all flex items-center gap-1.5 w-fit"
                  >
                    {formData.profileImage ? "Change Photo" : "Upload Photo"}
                  </button>
                  {errors.profileImage && (
                    <p className="text-red-500 text-xs font-medium">
                      {errors.profileImage}
                    </p>
                  )}
                </div>
                <div className="text-gray-500 text-[11px] space-y-0.5 leading-relaxed">
                  <p>Max size: 5MB</p>
                  <p>Format: JPG, PNG, GIF, WEBP</p>
                </div>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-5">
            <div className="flex flex-col gap-2">
              <label className="form-label">
                First Name <span className="text-red-500">*</span>
              </label>
              <input
                value={formData.firstName}
                onChange={(e) => {
                  setFormData({ ...formData, firstName: e.target.value });
                  if (errors.firstName)
                    setErrors((prev) => {
                      const { firstName, ...rest } = prev;
                      return rest;
                    });
                }}
                className={`form-input ${errors.firstName ? "border-red-500 ring-1 ring-red-500" : ""}`}
                type="text"
                placeholder="First name"
              />
              {errors.firstName && (
                <p className="text-red-500 text-[11px] font-medium">
                  {errors.firstName}
                </p>
              )}
            </div>
            <div className="flex flex-col gap-2">
              <label className="form-label">Last Name</label>
              <input
                value={formData.lastName}
                onChange={(e) =>
                  setFormData({ ...formData, lastName: e.target.value })
                }
                className="form-input"
                type="text"
                placeholder="Last name"
              />
            </div>
            <div className="flex flex-col gap-2 col-span-2">
              <label className="form-label">
                Email Address <span className="text-red-500">*</span>
              </label>
              <input
                disabled
                value={formData.email}
                className="form-input bg-gray-50 text-gray-500 cursor-not-allowed"
                type="email"
              />
            </div>
            <div className="flex flex-col gap-2 col-span-2">
              <label className="form-label">Company Name</label>
              <input
                value={formData.company}
                onChange={(e) =>
                  setFormData({ ...formData, company: e.target.value })
                }
                className="form-input"
                type="text"
                placeholder="Enter company name"
              />
            </div>
            <div className="flex flex-col gap-2">
              <label className="form-label">Designation</label>
              <input
                value={formData.designation}
                onChange={(e) =>
                  setFormData({ ...formData, designation: e.target.value })
                }
                className="form-input"
                type="text"
                placeholder="e.g. CEO, Developer"
              />
            </div>
            <div className="flex flex-col gap-2">
              <label className="form-label">Phone Number</label>
              <PhoneInput
                country={"in"}
                value={formData.phone}
                onChange={(val) => setFormData({ ...formData, phone: val })}
                inputClass="!w-full !h-11 !text-sm !border-gray-300 !rounded-lg focus:!ring-2 focus:!ring-primary-bg-color focus:!border-primary-bg-color !transition-all"
                buttonClass="!border-gray-300 !rounded-l-lg !bg-white"
                containerClass="!w-full"
                inputStyle={{
                  width: "100%",
                  height: "44px",
                  borderRadius: "8px",
                }}
                dropdownStyle={{
                  bottom: "100%",
                  top: "auto",
                  marginBottom: "5px",
                  borderRadius: "12px",
                  boxShadow: "0 10px 15px -3px rgb(0 0 0 / 0.1)",
                }}
                placeholder="Enter phone number"
              />
            </div>
          </div>
        </section>

        {/* Right Column */}
        <section className="flex flex-col gap-5">
          <div className="flex flex-col flex-grow">
            <div className="flex justify-between items-center mb-2">
              <label className="form-label">About Me / Bio</label>
              <span className="text-[11px] text-gray-400 font-medium">
                {formData.about?.replace(/<[^>]*>/g, "").length || 0}/10000
              </span>
            </div>
            <div className="flex-grow border border-gray-300 rounded-xl overflow-hidden focus-within:ring-2 focus-within:ring-primary-bg-color/20 focus-within:border-primary-bg-color transition-all bg-white flex flex-col h-[372px]">
              <style>{`
                .ck.ck-editor { display: flex; flex-direction: column; flex-grow: 1; border: none !important; }
                .ck.ck-editor__main { flex-grow: 1; display: flex; flex-direction: column; }
                .ck-editor__editable { flex-grow: 1 !important; min-height: 280px !important; padding: 16px 20px !important; line-height: 1.6 !important; font-size: 14px !important; font-weight: 400 !important; color: #111827 !important; border: none !important; }
                .ck.ck-toolbar { border: none !important; border-bottom: 1px solid #f3f4f6 !important; background-color: #f9fafb !important; padding: 6px 10px 2px !important; }
                .ck.ck-button { border-radius: 6px !important; margin: 0 1px !important; }
                .ck.ck-button.ck-on { background: #e2e8f0 !important; color: #435e70 !important; }
                .ck.ck-content .ck-placeholder::before { color: #9ca3af !important; font-style: normal !important; }
              `}</style>
              <CKEditor
                editor={ClassicEditor}
                data={formData.about}
                onChange={(e, editor) =>
                  setFormData({ ...formData, about: editor.getData() })
                }
                config={{
                  placeholder:
                    "Write a brief introduction about yourself or your role...",
                  toolbar: [
                    "paragraph",
                    "fontFamily",
                    "fontSize",
                    "bold",
                    "italic",
                    "underline",
                    "|",
                    "alignment",
                    "bulletedList",
                    "numberedList",
                    "|",
                    "link",
                    "blockQuote",
                    "undo",
                    "redo",
                  ],
                }}
              />
            </div>
          </div>

          <div className="flex flex-col gap-2">
            <label className="form-label">Social Media Links</label>
            <div className="flex border border-gray-300 rounded-xl overflow-hidden bg-white focus-within:ring-2 focus-within:ring-primary-bg-color/20 focus-within:border-primary-bg-color transition-all h-[44px]">
              <div className="relative border-r border-gray-100 min-w-[120px] h-full flex items-center">
                <select
                  value={formData.selectedPlatform}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      selectedPlatform: e.target.value,
                    })
                  }
                  className="  w-full h-full pl-4 pr-10 text-sm font-medium 
      text-gray-700 bg-white rounded-lg
      border border-transparent
      outline-none cursor-pointer appearance-none
      transition-colors duration-300 ease-in-out

      hover:bg-gray-50"
                >
                  {[
                    "Facebook",
                    "LinkedIn",
                    "Instagram",
                    "Telegram",
                    "Medium",
                    "TikTok",
                    "YouTube",
                    "X",
                  ].map((p) => (
                    <option key={p} value={p}>
                      {p}
                    </option>
                  ))}
                </select>
                <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
              </div>
              <input
                value={formData.social}
                onChange={(e) => {
                  setFormData({ ...formData, social: e.target.value });
                  if (errors.social)
                    setErrors((prev) => {
                      const { social, ...rest } = prev;
                      return rest;
                    });
                }}
                placeholder={`${formData.selectedPlatform} Profile URL`}
                className={`flex-1 px-4 text-sm font-medium text-gray-900 outline-none placeholder:text-gray-400 ${errors.social ? "placeholder:text-red-300" : ""}`}
                type="text"
              />
              <button
                onClick={handleAddSocialLink}
                className="px-6 border-l border-gray-100 text-xs font-bold text-primary-bg-color hover:bg-primary-bg-color/5 uppercase tracking-widest transition-colors flex items-center justify-center"
              >
                Add
              </button>
            </div>
            {errors.social && (
              <p className="text-red-500 text-[11px] font-medium px-1">
                {errors.social}
              </p>
            )}

            <div className="flex flex-col gap-2 max-h-[80px] overflow-y-auto pr-1 custom-scrollbar">
              {formData.socialLinks.map((link, idx) => (
                <div
                  key={idx}
                  className="flex items-center bg-gray-50 border border-gray-200 rounded-lg overflow-hidden h-9 w-full flex-shrink-0"
                >
                  <div className="w-10 h-full bg-primary-bg-color flex items-center justify-center flex-shrink-0">
                    {link.platform === "X" ? (
                      <XIcon />
                    ) : (
                      <Globe className="w-4 h-4 text-white" />
                    )}
                  </div>
                  <span className="flex-1 px-3 text-xs font-medium text-gray-600 truncate">
                    {link.url}
                  </span>
                  <button
                    onClick={() => removeSocialLink(idx)}
                    className="p-2 text-gray-400 hover:text-red-500 transition-colors"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
          </div>
        </section>
      </div>
    </Modal>
  );

  const renderAdditionalInfo = () => (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      maxWidth="max-w-5xl"
      title={<Header currentStep={1} />}
      footer={<Footer />}
    >
      <div className="flex flex-col items-center max-w-2xl mx-auto py-12 min-h-[480px]">
        <div className="w-16 h-16 bg-primary-bg-color/10 rounded-2xl flex items-center justify-center text-primary-bg-color mb-6 ring-8 ring-primary-bg-color/5">
          <Globe className="w-8 h-8" />
        </div>
        <h3 className="section-heading text-xl text-center">
          Additional Information
        </h3>
        <p className="section-subheading text-center mb-10 max-w-md">
          Provide more details to help attendees and partners find you easily
          during the event.
        </p>

        <div className="w-full flex flex-col gap-6">
          {additionalFields.length > 0 && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5 w-full">
              {additionalFields.map((field) => (
                <div
                  key={field.id}
                  className={`flex flex-col gap-2 ${field.type === "textarea" ? "md:col-span-2" : ""}`}
                >
                  <ExhibitorReqFormField
                    field={field}
                    value={formData.additional_info[field.name]}
                    disabled={(siteUser?.role_id || siteUser?.roleId || siteUser?.booth_member?.role_id) !== 23}
                    onChange={(id, val) => {
                      setFormData((prev) => ({
                        additional_info: {
                          ...prev.additional_info,
                          [field.name]: val,
                        },
                      }));
                    }}
                    onFileChange={(id, file) => {
                      if (file) {
                        const isImage = field.type?.toLowerCase() === "image" || field.field_type?.toLowerCase() === "image";
                        const fileError = validateFile(file, isImage);
                        if (fileError) {
                          toast.error(fileError);
                          return;
                        }
                      }
                      setFormData((prev) => ({
                        ...prev,
                        additional_info: {
                          ...prev.additional_info,
                          [field.name]: file,
                        },
                      }));
                    }}
                  />
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </Modal>
  );

  const renderPrivacy = () => (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      maxWidth="max-w-5xl"
      title={<Header currentStep={2} />}
      footer={<Footer />}
    >
      <div className="max-w-2xl mx-auto py-16 min-h-[480px]">
        <div className="flex items-center gap-7 mb-12">
          <div className="w-16 h-16 bg-primary-bg-color/10 rounded-2xl flex items-center justify-center text-primary-bg-color ring-8 ring-primary-bg-color/5">
            <Share2 className="w-8 h-8" />
          </div>
          <div>
            <h3 className="section-heading text-xl">Privacy & Data Sharing</h3>
            <p className="section-subheading max-w-sm">
              Manage how your professional information is shared with other
              event participants.
            </p>
          </div>
        </div>

        <div className="bg-gray-50/80 rounded-3xl p-8 border border-gray-100 shadow-sm">
          <div className="flex items-center justify-between gap-10">
            <div className="flex-1">
              <h4 className="text-base font-bold text-gray-900 mb-1.5 flex items-center gap-2">
                Enable Data Sharing
                <div className="w-1.5 h-1.5 rounded-full bg-primary-bg-color" />
              </h4>
              <p className="text-sm text-gray-500 leading-relaxed font-medium">
                When enabled, your profile information can be shared with others
                via your digital badge (QR code) or during meetings.
              </p>
            </div>
            <button
              onClick={() =>
                setFormData({ ...formData, dataShare: !formData.dataShare })
              }
              className={`w-16 h-9 rounded-full relative transition-all duration-300 flex items-center px-1.5 shadow-inner flex-shrink-0 ${formData.dataShare ? "bg-primary-bg-color" : "bg-gray-200"}`}
            >
              <div
                className={`w-6 h-6 bg-white rounded-full transition-all shadow-md transform ${formData.dataShare ? "translate-x-7" : "translate-x-0"}`}
              />
            </button>
          </div>
        </div>

      </div>
    </Modal>
  );

  switch (step) {
    case 0:
      return renderInfo();
    case 1:
      return renderAdditionalInfo();
    case 2:
      return renderPrivacy();
    default:
      return null;
  }
}

export default ExhibitorProfileSettings;
